"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { clearSessionAndRedirect } from "../utils/cleanup";
import { useTranslation } from "react-i18next";
import { createLogger } from "@/app/lib/logger";
import { getLocaleFromPath, addLocaleToPath } from "@/app/lib/i18n-config";

const log = createLogger("LogoutButton");

interface LogoutButtonProps {
  className?: string;
  children?: React.ReactNode;
  /** Drop the default red pill so `className` fully styles the button. */
  bare?: boolean;
}

const DEFAULT_CLASSES =
  "px-5 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export default function LogoutButton({
  className = "",
  children,
  bare = false,
}: LogoutButtonProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = async () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);

    try {
      // Call our logout API to logout from backend
      await fetch("/api/auth/logout", {
        method: "POST",
      });
    } catch (error) {
      log.error("Backend logout failed", error);
      // Continue with frontend logout even if backend fails
    }

    try {
      // Use unified session clearing and redirect
      await clearSessionAndRedirect(t("auth.login.logoutMessage"));
    } catch (error) {
      log.error("Session clearing failed", error);
      // Fallback to manual redirect with locale
      const locale = getLocaleFromPath(pathname);
      const loginPath = addLocaleToPath("/login", locale);
      window.location.href = `${loginPath}?message=${encodeURIComponent(t("auth.login.logoutMessage"))}`;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleLogout}
      disabled={isLoading}
      className={bare ? className : `${DEFAULT_CLASSES} ${className}`}
    >
      {isLoading ? t("logout.loggingOut") : children || t("logout.logout")}
    </button>
  );
}
